import React, { useCallback, useMemo } from "react";
import type { Project } from "../../../../../../../../../api/useProjects";
import ModalForm from "@ui/ModalV2/Form";
import t from "../../../../../../../../../i18n";
import Text from "@ui/Text";
import { colors, spacings } from "@ui/Theme";
import { css } from "@linaria/core";
import formatDate from "@ui/i18n/formatDate";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import useToasts from "@ui/Toast/useToasts";
import { useDelete } from "../../../../../../../../../api/useCall";

interface Impact {
  quantityId: string;
  quantityName: string;
  unit: string | null;
  value: number;
}
interface DeleteModalFormProps {
  project: Project;
  manualFaultEventId: string;
  manualFaultEventStartsAt: number;
  timeWindow: {
    id: string;
    startDate: number;
    endDate: number;
    impacts: Impact[];
  };
  closeModal: () => void;
}

const DeleteModalForm = ({
  project,
  manualFaultEventId,
  manualFaultEventStartsAt,
  timeWindow,
  closeModal,
}: DeleteModalFormProps) => {
  const { addToast } = useToasts();
  const deleteTimeWindow = useDelete(
    "/projects/:projectId(uuid)/manualFaultEvents/:manualFaultEventId(uuid)/windows/:windowId(uuid)",
  );

  const isFirtsTimeWindow = useMemo(
    () => manualFaultEventStartsAt === timeWindow.startDate,
    [manualFaultEventStartsAt, timeWindow.startDate],
  );

  const handleSubmit = useCallback(async () => {
    try {
      await deleteTimeWindow({
        routeParams: {
          projectId: project.id,
          manualFaultEventId,
          windowId: timeWindow.id,
        },
      });
      addToast("success", t("project.event.manual_fault.modalForm.time_window.delete.success"));
      closeModal();
      return true;
    } catch (error) {
      addToast("error", t("errors.backend_error"));
      return false;
    }
  }, [addToast, closeModal, deleteTimeWindow, manualFaultEventId, project.id, timeWindow.id]);

  return (
    <ModalForm
      className={overrideModal}
      cancellable
      onRequestClose={closeModal}
      onSubmit={handleSubmit}
      okButtonName={t("project.events.modalForm.okButton.delete")}
    >
      <Text>{t("project.event.manual_fault.modalForm.time_window.delete.description")}</Text>

      <div className={periodCss}>
        <Text type="body1SemiBold" className={title}>
          {t("project.events.manualFaultEvent.modifyModalForm.impactedPeriod")}
        </Text>
        <Text>
          {formatDate(timeWindow.startDate, "slash", project.timezone)} -{" "}
          {formatDate(timeWindow.endDate, "slash", project.timezone)}
        </Text>
      </div>

      <div className={impactPIs}>
        <Text type="body1SemiBold" className={title}>
          {t("project.events.manualFaultEvent.modifyModalForm.impactedPIs")}
        </Text>
        {timeWindow.impacts.map(impact => (
          <div key={impact.quantityId}>
            <Text
              className={title}
              type="body1SemiBold"
            >{`${impact.quantityName} ${impact.unit && `(${impact.unit})`}`}</Text>
            <Text key={impact.quantityId}>{impact.value}</Text>
          </div>
        ))}
      </div>

      <AdditionnalMessage
        status="WARNING"
        text={
          isFirtsTimeWindow
            ? t(
                "project.event.manual_fault.modalForm.time_window.delete.message.warning.next_period",
              )
            : t(
                "project.event.manual_fault.modalForm.time_window.delete.message.warning.previous_period",
              )
        }
      />
    </ModalForm>
  );
};

export default DeleteModalForm;

//#region CSS
const overrideModal = css`
  row-gap: ${spacings.l};
`;
const title = css`
  color: ${colors.carbon800};
`;
const periodCss = css`
  & > :last-child {
    margin-top: ${spacings.xxs};
  }
`;
const impactPIs = css`
  display: flex;
  flex-direction: column;
  row-gap: ${spacings.m};
`;
//#endregion CSS
